import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatNaira } from "@/lib/api";
import { Check } from "lucide-react";

export default function ReceiptPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setOrder(false));
  }, [id]);

  if (order === null) return <div className="p-16 text-center">Loading…</div>;
  if (order === false) return <div className="p-16 text-center">Order not found.</div>;

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16" data-testid="receipt-page">
      <div className="text-center mb-12">
        <div className="w-16 h-16 mx-auto bg-[#FED700] flex items-center justify-center mb-6">
          <Check size={28} />
        </div>
        <p className="label-eyebrow mb-2">Order {order.status === "paid" ? "Confirmed" : "Placed"}</p>
        <h1 className="font-serif text-4xl tracking-tight">Thank you</h1>
        <p className="text-[#6A747D] mt-3">Your order <span className="font-medium text-[#333E48]" data-testid="receipt-order-id">{order.id}</span> is being prepared.</p>
      </div>

      <div className="border border-[#E8E2D9] p-8" data-testid="receipt-body">
        <div className="flex items-baseline justify-between mb-8 flex-wrap gap-2">
          <h2 className="font-serif text-2xl">Ajoyo Store</h2>
          <span className="text-xs text-[#6A747D] uppercase tracking-wider">{new Date(order.created_at).toLocaleString("en-NG")}</span>
        </div>

        <div className="space-y-4 mb-6">
          {order.items.map((it, idx) => (
            <div key={idx} className="flex justify-between text-sm gap-4">
              <div>
                <p className="font-medium">{it.product.name}</p>
                <p className="text-[#6A747D]">Qty: {it.quantity} × {formatNaira(it.product.price)}</p>
              </div>
              <span>{formatNaira(it.line_total)}</span>
            </div>
          ))}
        </div>

        <div className="border-t border-[#E8E2D9] pt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span>{formatNaira(order.subtotal)}</span></div>
          <div className="flex justify-between"><span>Shipping</span><span>{order.shipping === 0 ? "Free" : formatNaira(order.shipping)}</span></div>
          <div className="flex justify-between font-serif text-xl pt-3 border-t border-[#E8E2D9] mt-3"><span>Total</span><span>{formatNaira(order.total)}</span></div>
        </div>

        <div className="mt-8 pt-8 border-t border-[#E8E2D9] grid sm:grid-cols-2 gap-6 text-sm">
          <div>
            <p className="label-eyebrow mb-2">Ship To</p>
            <p>{order.shipping_address.full_name}</p>
            <p>{order.shipping_address.address}</p>
            <p>{order.shipping_address.city}, {order.shipping_address.state}</p>
            <p>{order.shipping_address.country}</p>
            <p className="mt-1 text-[#6A747D]">{order.shipping_address.phone}</p>
          </div>
          <div>
            <p className="label-eyebrow mb-2">Payment</p>
            <p>Method: {order.payment_method}</p>
            <p>Status: <span className="capitalize">{order.status}</span></p>
            {order.paystack_reference && <p className="text-[#6A747D] mt-1 break-all">Ref: {order.paystack_reference}</p>}
          </div>
        </div>
      </div>

      <div className="flex gap-3 justify-center mt-10">
        <Link to="/shop" className="btn-gold" data-testid="receipt-continue-btn">Continue Shopping</Link>
        <Link to="/profile" className="btn-outline">View All Orders</Link>
      </div>
    </div>
  );
}
